import { Cable, Info } from 'lucide-react'
import { FC } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { z } from 'zod'

import Field from '@/components/field'
import LoadingButton from '@/components/loading-button'
import { FormProps } from '@/pages/settings/logging'
import TestRapid7 from '@/pages/settings/logging/rapid7/test'

import { zodResolver } from '@hookform/resolvers/zod'
import { DialogFooter } from '@/components/ui/dialog'

const schema = z
  .object({
    webhookUrl: z.string().min(1, { message: 'Webhook URL is required' }),
  })
  .required()

type FormData = z.infer<typeof schema>

type Props = FormProps<'rapid7'>

const Rapid7Form: FC<Props> = ({ onSetup, setupTimer, onTest, testTimer }) => {
  const form = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      webhookUrl: '',
    },
    mode: 'onChange',
  })

  return (
    <FormProvider {...form}>
      <form onSubmit={form.handleSubmit(onSetup)}>
        <Field<FormData> label="Webhook URL" name="webhookUrl" placeholder="Enter your Rapid7 Webhook URL" />

        <div className="flex items-start gap-3 p-4 my-4 text-xs border rounded-lg bg-muted/50 border-muted">
          <Info size={16} />
          <div>
            <p className="mb-2 font-medium text-primary">To generate the Webhook URL, follow these steps:</p>
            <ol className="space-y-1 list-decimal list-inside text-muted-foreground">
              <li>Go to InsightIDR</li>
              <li>Navigate to Data Collection</li>
              <li>Click on Event Sources</li>
              <li>Click Add Event Source</li>
              <li>
                Search for <span className="font-semibold text-foreground">&apos;Rapid7 Custom Logs&apos;</span>
              </li>
              <li>
                Select <span className="font-semibold text-foreground">&apos;Webhook&apos;</span>
              </li>
              <li>
                Leave <span className="italic">JSON Events Key</span> blank
              </li>
              <li>Copy &amp; Paste the Webhook URL</li>
            </ol>
          </div>
        </div>

        <DialogFooter className="flex gap-4">
          <TestRapid7 timer={testTimer} onTest={() => onTest(form.getValues())} />
          <LoadingButton timer={setupTimer} type="submit" className="w-full">
            <Cable className="w-4 h-4 mr-2" />
            {setupTimer.isLoading ? 'Connecting...' : 'Connect Rapid7'}
          </LoadingButton>
        </DialogFooter>
      </form>
    </FormProvider>
  )
}

export default Rapid7Form
